/**
 * 🎥 Video utility functions for validation and error handling
 * Used by NewGamePageHeader to validate video URLs and handle playback errors
 */

/**
 * Extracts YouTube video ID from various YouTube URL formats
 * @param url - The YouTube URL to parse
 * @returns The video ID or empty string if invalid
 */
export const getYoutubeVideoId = (url: string | undefined): string => {
  if (!url) {
    return ''
  }

  try {
    const youtubeUrl = new RegExp(
      '^(https?://)?(www.youtube.com|m.youtube.com|youtu.?be)/.+$'
    )

    if (!youtubeUrl.test(url)) {
      return ''
    }

    const urlObj = new URL(url)
    const hostname = urlObj.hostname

    if (
      hostname.includes('youtube.com') ||
      hostname.includes('m.youtube.com')
    ) {
      return urlObj.searchParams.get('v') || ''
    } else if (hostname.includes('youtu.be')) {
      return urlObj.pathname.slice(1) // Remove the leading '/'
    }

    return ''
  } catch (error) {
    console.error('❌ getYoutubeVideoId: Failed to parse URL', { url, error })
    return ''
  }
}

/**
 * Validates if a YouTube URL is properly formatted and has a valid video ID
 * @param url - The YouTube URL to validate
 * @returns True if the URL is valid, false otherwise
 */
export const isValidYoutubeUrl = (url: string | undefined): boolean => {
  if (!url) {
    return false
  }

  const videoId = getYoutubeVideoId(url)
  return videoId !== '' && videoId.length >= 11 // YouTube video IDs are typically 11 characters
}

/**
 * Validates if an S3 video URL is properly formatted
 * @param url - The S3 URL to validate
 * @returns True if the URL appears to be a valid S3 video URL, false otherwise
 */
export const isValidS3VideoUrl = (url: string | undefined): boolean => {
  if (!url) {
    return false
  }

  try {
    const urlObj = new URL(url)
    const isS3 =
      urlObj.hostname.includes('s3.amazonaws.com') ||
      urlObj.hostname.includes('amazonaws.com')

    // Check for common video file extensions
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi']
    const hasVideoExtension = videoExtensions.some((ext) =>
      urlObj.pathname.toLowerCase().includes(ext)
    )

    return isS3 && hasVideoExtension
  } catch (error) {
    console.error('❌ isValidS3VideoUrl: Failed to parse URL', { url, error })
    return false
  }
}

/**
 * Validates if a URL is a valid HLS/streaming URL
 * Supports common streaming providers like Mux, Cloudflare, etc.
 * @param url - The streaming URL to validate
 * @returns True if the URL appears to be a valid streaming URL, false otherwise
 */
export const isValidStreamingUrl = (url: string | undefined): boolean => {
  if (!url) {
    return false
  }

  try {
    const urlObj = new URL(url)

    // Check for HLS manifest files
    const isHLS = urlObj.pathname.toLowerCase().endsWith('.m3u8')

    // Check for common streaming providers
    const streamingProviders = [
      'mux.com',
      'stream.mux.com',
      'cloudflarestream.com',
      'akamaihd.net',
      'fastly.net',
      'cdn.jwplayer.com',
      'bitmovin.com',
    ]
    const isKnownProvider = streamingProviders.some((provider) =>
      urlObj.hostname.includes(provider)
    )

    // Valid if it's an HLS stream OR from a known streaming provider
    return isHLS || isKnownProvider
  } catch (error) {
    console.error('❌ isValidStreamingUrl: Failed to parse URL', { url, error })
    return false
  }
}

/**
 * Validates if a video URL is valid (YouTube, S3, or streaming URL)
 * @param url - The video URL to validate
 * @returns True if the URL is valid, false otherwise
 */
export const isValidVideoUrl = (url: string | undefined): boolean => {
  if (!url) {
    return false
  }

  const isS3 = url.includes('s3.amazonaws.com')

  if (isS3) {
    return isValidS3VideoUrl(url)
  }

  // Check for streaming URLs (HLS, Mux, etc.)
  if (isValidStreamingUrl(url)) {
    return true
  }

  // Default to YouTube validation
  return isValidYoutubeUrl(url)
}
